import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type { ChangeListItem } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { labelChangeStatus } from '../changeLabels';
import { addDays, formatDateInput } from '../hooks/useCalendarWindow';

type DatePreset = 'all' | 'today' | 'week' | 'month' | 'custom';
type DateScope = 'any' | 'go_live' | 'riscos' | 'camud';

interface Props {
  show: boolean;
  meetingId: number;
  alreadyIncludedIds: number[];
  onHide: () => void;
  onAdded: (meeting: Awaited<ReturnType<typeof changesApi.addMeetingItems>>) => void;
}

interface DateRange {
  from: string;
  to: string;
}

const PAGE_SIZE = 50;

function SortableSelected({
  item,
  index,
}: {
  item: ChangeListItem;
  index: number;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  return (
    <div
      ref={setNodeRef}
      className="d-flex align-items-center gap-2 border rounded px-2 py-1 mb-1 bg-white"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        cursor: 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <span className="text-muted" aria-hidden="true">
        <i className="bi bi-grip-vertical" />
      </span>
      <span>
        {index + 1}. #{item.id} {item.title}
      </span>
    </div>
  );
}

function todayLocal(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function rangeForPreset(preset: Exclude<DatePreset, 'custom' | 'all'>, anchor = todayLocal()): DateRange {
  if (preset === 'today') {
    const iso = formatDateInput(anchor);
    return { from: iso, to: iso };
  }
  if (preset === 'week') {
    const weekday = anchor.getDay();
    const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
    const start = addDays(anchor, mondayOffset);
    return { from: formatDateInput(start), to: formatDateInput(addDays(start, 6)) };
  }
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { from: formatDateInput(start), to: formatDateInput(end) };
}

const ChangeCabAddChangesModal: React.FC<Props> = ({
  show,
  meetingId,
  alreadyIncludedIds,
  onHide,
  onAdded,
}) => {
  const [search, setSearch] = useState('');
  const [preset, setPreset] = useState<DatePreset>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateScope, setDateScope] = useState<DateScope>('any');
  const [results, setResults] = useState<ChangeListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [picked, setPicked] = useState<Record<number, ChangeListItem>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const already = useMemo(() => new Set(alreadyIncludedIds), [alreadyIncludedIds]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(
    async (overrides?: {
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      dateScope?: DateScope;
      skip?: number;
      append?: boolean;
    }) => {
      const term = overrides?.search ?? search;
      const from = overrides?.dateFrom ?? dateFrom;
      const to = overrides?.dateTo ?? dateTo;
      const scope = overrides?.dateScope ?? dateScope;
      const skip = overrides?.skip ?? 0;
      setLoading(true);
      setError(null);
      try {
        const data = await changesApi.list({
          search: term.trim() || undefined,
          limit: PAGE_SIZE,
          skip,
          date_from: from || undefined,
          date_to: to || undefined,
          date_scope: from || to ? scope : undefined,
        });
        const items = data.items || [];
        setResults((prev) => (overrides?.append ? [...prev, ...items] : items));
        setTotal(data.total || 0);
      } catch (err: unknown) {
        if (!overrides?.append) {
          setResults([]);
          setTotal(0);
        }
        setError(getApiErrorMessage(err, 'Falha ao buscar mudanças.'));
      } finally {
        setLoading(false);
      }
    },
    [search, dateFrom, dateTo, dateScope],
  );

  useEffect(() => {
    if (!show) return;
    setSearch('');
    setPreset('all');
    setDateFrom('');
    setDateTo('');
    setDateScope('any');
    setSelectedIds([]);
    setPicked({});
    setError(null);
    void load({ search: '', dateFrom: '', dateTo: '', dateScope: 'any', skip: 0 });
  }, [show]);

  const applyPreset = (next: Exclude<DatePreset, 'custom'>) => {
    setPreset(next);
    if (next === 'all') {
      setDateFrom('');
      setDateTo('');
      void load({ dateFrom: '', dateTo: '', skip: 0 });
      return;
    }
    const range = rangeForPreset(next);
    setDateFrom(range.from);
    setDateTo(range.to);
    void load({ dateFrom: range.from, dateTo: range.to, skip: 0 });
  };

  const selectedItems = selectedIds
    .map((id) => picked[id] || results.find((item) => item.id === id))
    .filter((item): item is ChangeListItem => Boolean(item));

  const toggle = (item: ChangeListItem, checked: boolean) => {
    if (already.has(item.id)) return;
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(item.id) ? prev : [...prev, item.id];
      }
      return prev.filter((id) => id !== item.id);
    });
    setPicked((prev) => {
      if (checked) {
        return { ...prev, [item.id]: item };
      }
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
  };

  const handleSelectedDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSelectedIds((prev) => {
      const oldIndex = prev.indexOf(Number(active.id));
      const newIndex = prev.indexOf(Number(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleConfirm = async () => {
    if (!selectedIds.length) {
      setError('Selecione ao menos uma mudança.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onAdded(await changesApi.addMeetingItems(meetingId, selectedIds));
      onHide();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não incluiu as mudanças na pauta.'));
    } finally {
      setSaving(false);
    }
  };

  const presetVariant = (value: DatePreset) => (preset === value ? 'primary' : 'outline-primary');

  return (
    <Modal show={show} onHide={onHide} size="lg" animation={false}>
      <Modal.Header closeButton>
        <Modal.Title>Adicionar mudanças à pauta</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">
          Não lista todas de uma vez: mostra até {PAGE_SIZE} por página, das que você pode ver.
          Use busca, hoje, semana, mês ou um período. A data considera Go Live, Riscos e Impactos
          ou CAMUD, conforme o recorte.
        </p>
        {error ? (
          <Alert variant="danger" className="py-2" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        <Form
          className="mb-3"
          onSubmit={(event) => {
            event.preventDefault();
            void load({ skip: 0 });
          }}
        >
          <div className="d-flex flex-wrap gap-2 mb-2">
            <Button size="sm" variant={presetVariant('all')} onClick={() => applyPreset('all')}>
              Todas
            </Button>
            <Button size="sm" variant={presetVariant('today')} onClick={() => applyPreset('today')}>
              Hoje
            </Button>
            <Button size="sm" variant={presetVariant('week')} onClick={() => applyPreset('week')}>
              Esta semana
            </Button>
            <Button size="sm" variant={presetVariant('month')} onClick={() => applyPreset('month')}>
              Este mês
            </Button>
          </div>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small mb-1">Busca</Form.Label>
              <Form.Control
                placeholder="Título, projeto ou texto"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Buscar mudanças"
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">De</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                aria-label="Data inicial"
                onChange={(event) => {
                  setPreset('custom');
                  setDateFrom(event.target.value);
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Até</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                aria-label="Data final"
                onChange={(event) => {
                  setPreset('custom');
                  setDateTo(event.target.value);
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Qual data</Form.Label>
              <Form.Select
                value={dateScope}
                aria-label="Recorte de data"
                onChange={(event) => setDateScope(event.target.value as DateScope)}
              >
                <option value="any">Qualquer</option>
                <option value="go_live">Go Live</option>
                <option value="riscos">Riscos e Impactos</option>
                <option value="camud">CAMUD</option>
              </Form.Select>
            </Col>
            <Col md="auto">
              <Button type="submit" variant="outline-primary" disabled={loading}>
                Filtrar
              </Button>
            </Col>
          </Row>
        </Form>
        <p className="small text-muted">
          {loading ? 'Carregando mudanças…' : `Mostrando ${results.length} de ${total} mudanças.`}
        </p>
        {!loading && results.length === 0 ? (
          <Alert variant="light" className="border py-2">
            Nenhuma mudança neste recorte. Mude a data, a busca ou escolha Todas.
          </Alert>
        ) : null}
        <div className="border rounded mb-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
          {results.map((item) => {
            const included = already.has(item.id);
            const checked = included || selectedIds.includes(item.id);
            const inputId = `cab-add-change-${item.id}`;
            return (
              <label
                key={item.id}
                htmlFor={inputId}
                className="d-flex align-items-start gap-2 px-3 py-2 border-bottom mb-0"
                style={{ cursor: included ? 'not-allowed' : 'pointer' }}
              >
                <input
                  id={inputId}
                  type="checkbox"
                  className="form-check-input flex-shrink-0"
                  style={{ position: 'static', float: 'none', margin: '0.35rem 0 0' }}
                  disabled={included}
                  checked={checked}
                  onChange={(event) => toggle(item, event.target.checked)}
                />
                <span className="min-w-0">
                  <strong>#{item.id}</strong> {item.title}
                  <span className="d-block small text-muted">
                    {labelChangeStatus(String(item.status))}
                    {item.project ? ` · ${item.project}` : ''}
                    {included ? ' · Já na pauta' : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        {results.length < total ? (
          <Button
            size="sm"
            variant="outline-secondary"
            className="mb-3"
            disabled={loading}
            onClick={() => void load({ skip: results.length, append: true })}
          >
            Carregar mais
          </Button>
        ) : null}
        <div>
          <div className="fw-semibold mb-2">
            Selecionadas ({selectedIds.length}) — arraste para definir a ordem na pauta
          </div>
          {selectedItems.length === 0 ? (
            <p className="small text-muted mb-0">Nenhuma selecionada ainda.</p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSelectedDragEnd}
            >
              <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                {selectedItems.map((item, index) => (
                  <SortableSelected key={item.id} item={item} index={index} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={() => void handleConfirm()} disabled={saving || !selectedIds.length}>
          {saving
            ? 'Incluindo…'
            : selectedIds.length === 1
              ? 'Incluir 1 mudança'
              : `Incluir ${selectedIds.length} mudanças`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeCabAddChangesModal;
